/* Copyright (c) 2017 Backpack Health LLC */

/* global window, document, Blob, Image, FileReader, URL, Uint8Array, DataView */
/* eslint-disable no-multi-spaces */
import { externalApi } from '../apiServices/ApiRequest';

function imageToDataUrl(image, callback) {
  const reader = new FileReader();
  reader.onload = () => (callback(reader.result));
  reader.readAsDataURL(image);
}

function toBase64(image) {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = (err) => rej(err);
    reader.readAsDataURL(image);
  })
}

function imageFromDataUrl(dataUrl, callback) {
  const image = new Image();
  image.onload = () => (callback(image));
  image.src = dataUrl;
}

function imageFromFile(file, callback) {
  const image = new Image();
  image.onload = () => (callback(image));
  image.src = URL.createObjectURL(file);
}

function arrayBufferFromUrl(imageUrl, callback) {
  function isDataURI(s) {
    return !!s.match(/^\s*data:([a-z]+\/[a-z]+(;[a-z-]+=[a-z-]+)?)?(;base64)?,[a-z0-9!$&',()*+,;=\-._~:@/?%\s]*\s*$/i);
  }

  function dataURItoBlob(dataURI) {
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
      byteString = window.atob(dataURI.split(',')[1]);
    } else {
      byteString = unescape(dataURI.split(',')[1]);
    }
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  }

  if (isDataURI(imageUrl)) {
    return callback(dataURItoBlob(imageUrl));
  }

  return externalApi('GET', imageUrl)
    .responseType('blob')
    .then(res => {
      callback(res.body);
    });
}

function imageOrientation(imageFile, callback) {
  const reader = new FileReader();
  reader.onload = (e) => {
    const view = new DataView(e.target.result);
    if (view.getUint16(0, false) !== 0xFFD8) return callback(-2);
    const length = view.byteLength;
    let offset = 2;
    while (offset < length) {
      const marker = view.getUint16(offset, false);
      offset += 2;
      if (marker === 0xFFE1) {
        const chunk = view.getUint32(offset += 2, false);
        if (chunk !== 0x45786966) {
          return callback(-1);
        }
        const little = view.getUint16(offset += 6, false) === 0x4949;
        offset += view.getUint32(offset + 4, little);
        const tags = view.getUint16(offset, little);
        offset += 2;
        for (let i = 0; i < tags; i++) {
          if (view.getUint16(offset + (i * 12), little) === 0x0112) {
            return callback(view.getUint16(offset + (i * 12) + 8, little));
          }
        }
      } else if ((marker & 0xFF00) !== 0xFF00) {
        break;
      } else {
        offset += view.getUint16(offset, false);
      }
    }
    return callback(-1);
  };
  reader.readAsArrayBuffer(imageFile);
}

function autoOrientImage(imageFile, callback) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  imageOrientation(imageFile, (orientation) => {
    imageFromFile(imageFile, image => {
      const width = image.width;
      const height = image.height;

      if (!orientation || orientation > 8) {
        imageToDataUrl(image, imageDataUrl => {
          callback(imageDataUrl);
        });
      }

      canvas.width = width;
      canvas.height = height;

      if (orientation > 4) {
        canvas.width = height;
        canvas.height = width;
      }

      switch (orientation) {
        case 2:   // horizontal flip
          ctx.translate(width, 0);
          ctx.scale(-1, 1);
          break;
        case 3:   // 180° rotate left
          ctx.translate(width, height);
          ctx.rotate(Math.PI);
          break;
        case 4:   // vertical flip
          ctx.translate(0, height);
          ctx.scale(1, -1);
          break;
        case 5:   // vertical flip + 90 rotate right
          ctx.rotate(0.5 * Math.PI);
          ctx.scale(1, -1);
          break;
        case 6:   // 90° rotate right
          ctx.rotate(0.5 * Math.PI);
          ctx.translate(0, -height);
          break;
        case 7:   // horizontal flip + 90 rotate right
          ctx.rotate(0.5 * Math.PI);
          ctx.translate(width, -height);
          ctx.scale(-1, 1);
          break;
        case 8:   // 90° rotate left
          ctx.rotate(-0.5 * Math.PI);
          ctx.translate(-width, 0);
          break;
        default: break;
      }

      ctx.drawImage(image, 0, 0, width, height);
      ctx.restore();

      callback(canvas.toDataURL(), width, height);
    });
  });
}

const ImageUtil = {
  imageToDataUrl,
  imageFromDataUrl,
  imageFromFile,
  imageOrientation,
  arrayBufferFromUrl,
  autoOrientImage,
  toBase64,
};

export default ImageUtil;
